import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import { VictoryChart, VictoryAxis, VictoryArea, VictoryLine, VictoryScatter, VictoryVoronoiContainer } from 'victory';
import Legend, { LegendProvider } from '@amzn/meridian/legend';
import victoryLinePlugin from '@amzn/meridian/legend/plugin/victory-line';
import victoryTooltipPlugin from '@amzn/meridian/legend/plugin/victory-tooltip';
import useVictoryTheme from '@amzn/meridian/use-victory-theme';
import merge from 'lodash/merge';

import DynamicShiftPlanBetaTable from './DynamicShiftPlanBetaTable';
import { chainWalk, convertToFixedDP, findInSortedList, generateIntervals } from '../../utils/helpers';

const DynamicShiftPlanBetaGraph = (props) => {
  // Graph state data
  const [graphData, setGraphData] = useState([]);
  const [maxMetricGraphValue, setMaxMetricGraphValue] = useState(0);

  // Victory graph control options
  const graphTheme = useVictoryTheme({ showIndependentGrid: false, interpolation: 'basis' });
  graphTheme.line.domainPadding.y = 30;
  graphTheme.scatter.domainPadding.y = 30;

  const PlannedDataSymbol = ({ color }) => (
    <svg viewBox="-1 -1 1 2" xmlns="http://www.w3.org/2000/svg" width={30} height={10} role="presentation">
      <g>
        <line x1="-3" y1="0" x2="-1" y2="0" stroke={color} strokeWidth="1" />
        <line x1="0" y1="0" x2="2" y2="0" stroke={color} strokeWidth="1" />
      </g>
    </svg>
  );

  const ActualDataSymbol = ({ color }) => (
    <svg viewBox="-1 -1 1 2" xmlns="http://www.w3.org/2000/svg" width={30} height={10} role="presentation">
      <g>
        <line x1="-3" y1="0" x2="0" y2="0" stroke={color} strokeWidth="1" />
        <line x1="0" y1="0" x2="2" y2="0" stroke={color} strokeWidth="1" />
      </g>
    </svg>
  );

  const GRAPH_LINES_LABELS = {
    volume: {
      actual: {
        Inducted: 'Actual Induct Volume',
        Stowed: 'Actual Stow Volume',
        Outbound: 'Actual Pick Stage Volume'
      },
      planned: {
        ...(props.isDynamicShiftPlanBetaEnabled && { Outbound: 'Planned Pick Stage Volume' })
      }
    },
    headcount: {
      actual: {
        Overall: 'Punch In Headcount',
        'Pick Stage': 'Actual Picking Headcount'
      },
      planned: {
        // Overall: 'Total Needed Headcount',
        Outbound: 'Needed Picking Headcount',
        Rostered: 'Expected Headcount'
      }
    },
    rate: {
      actual: {
        Sortation: 'Actual Sort Rate',
        Outbound: 'Actual Pick Stage Rate'
      },
      planned: {
        ...(props.isDynamicShiftPlanBetaEnabled && { 'Pick & Stage - Pick': 'Planned Pick Stage Rate' })
      }
    }
  };

  function getGraphLineColor(processType) {
    switch (processType) {
      case 'Outbound':
      case 'Pick Stage':
      case 'Pick & Stage - Pick':
        return '#5cbfb4';
      case 'Rostered':
        return '#f60ecfff';
      case 'Label':
      case 'Induct':
      case 'Inducted':
        return '#df6dd4';
      case 'Stow':
      case 'Stowed':
        return '#f4972b';
      default:
        return '#f4972b';
    }
  }

  // 1. Dynamically determine the dataset period.
  // 2. Generate x-values for missing periods.
  // 3. Generate datapoints if it's missing from the dataset.
  // 3a. Use the previous x-value as the current x-value.
  // 3b. But if it's not volume and at the end of the dataset, default to 0.
  //     This covers when Headcount or Rate have no data points since it drops to zero.
  const enrichDatasets = (datasets, metric) => {
    if (datasets.length === 0) return datasets;

    // Collect all time values and detect interval
    const allTimes = [];
    datasets.forEach((dataset) => {
      dataset.data.forEach((point) => {
        const timeValue =
          typeof point.startInSeconds === 'string'
            ? new Date(point.startInSeconds + ':00+00:00').getTime() / 1000
            : point.startInSeconds;
        allTimes.push(timeValue);
      });
    });

    allTimes.sort((a, b) => a - b);
    const minTime = allTimes[0];
    const maxTime = allTimes[allTimes.length - 1];

    // Detect interval from consecutive time differences
    const intervals = [];
    for (let i = 1; i < allTimes.length; i++) {
      const diff = allTimes[i] - allTimes[i - 1];
      if (diff > 0) intervals.push(diff);
    }
    // Assume 15m interval as default since that's the planned value interval.
    const intervalSeconds = intervals.length > 0 ? Math.min(...intervals) : 15 * 60;

    // Generate consistent intervals
    const consistentXValues = [];
    for (let time = minTime; time <= maxTime; time += intervalSeconds) {
      consistentXValues.push(time);
    }

    // Enrich each dataset
    return datasets.map((dataset) => {
      const enrichedData = [];
      let lastValue = null;
      const maxExistingX = chainWalk(() => {
        const lastPoint = dataset.data[dataset.data.length - 1];
        return typeof lastPoint.startInSeconds === 'string'
          ? new Date(lastPoint.startInSeconds + ':00+00:00').getTime() / 1000
          : lastPoint.startInSeconds;
      }, 0);

      consistentXValues.forEach((xValue, index, xValuesArray) => {
        const existingPoint = dataset.data.find((point) => {
          const pointTime =
            typeof point.startInSeconds === 'string'
              ? new Date(point.startInSeconds + ':00+00:00').getTime() / 1000
              : point.startInSeconds;
          return Math.abs(pointTime - xValue) < intervalSeconds / 2;
        });

        // Get the end time to be used for the new point
        const endXValue = index < xValuesArray.length - 1 ? xValuesArray[index + 1] : xValue;

        if (existingPoint) {
          enrichedData.push({
            ...existingPoint,
            startInSeconds: xValue,
            endInSeconds: endXValue
          });
          lastValue = existingPoint.value;
        } else if (lastValue !== null) {
          const isAfterLastExisting = xValue > maxExistingX;
          const valueToUse = !isAfterLastExisting ? lastValue : metric === 'volume' ? null : 0;
          enrichedData.push({
            startInSeconds: xValue,
            endInSeconds: endXValue,
            value: valueToUse
          });
        } else {
          enrichedData.push({
            startInSeconds: xValue,
            endInSeconds: endXValue,
            value: 0
          });
          lastValue = 0;
        }
      });
      return { ...dataset, data: enrichedData };
    });
  };

  // Returns graph datasets to be plotted on metric and process types
  const updateGraphDatasets = (metric, processTypes) => {
    const graphDatasets = [];
    const metricData = props.adherenceGraphData[metric] || {};
    processTypes.forEach((processType) => {
      const processKey = processType;
      const processData = metricData[processKey] || { actual: [], planned: [] };
      const lineColor = getGraphLineColor(processType);
      ['actual', 'planned'].forEach((dataCategory) => {
        if (GRAPH_LINES_LABELS[metric][dataCategory][processType]) {
          graphDatasets.push({
            key: dataCategory + processType + 'Data',
            label: GRAPH_LINES_LABELS[metric][dataCategory][processType],
            color: lineColor,
            symbol: dataCategory === 'planned' ? PlannedDataSymbol : ActualDataSymbol,
            data: processData[dataCategory]
          });
        }
      });
    });

    return enrichDatasets(graphDatasets, metric);
  };

  // Returns the style to be used by a line graph based on the data category
  // and process type included in the provided key.
  const getLineGraphStyle = (getLineProps, key) => {
    const currentStyle = getLineProps(key).style;
    const isPlannedData = key.startsWith('planned');
    const customizedProps = {
      data: {
        strokeWidth: 2,
        strokeDasharray: isPlannedData ? '10, 10' : ''
      }
    };
    return merge(currentStyle, customizedProps);
  };

  // Add a point to the dataset matching the breakItem
  const addBreakDatapointsToDataset = (dataset, breaksData) => {
    const extraBreakPoints = [];
    (breaksData || []).forEach((breakItem) => {
      ['startInSeconds', 'endInSeconds'].forEach((timeType) => {
        const pointSearchResult = findInSortedList(dataset['data'], breakItem[timeType], 'startInSeconds');
        if (!pointSearchResult['found']) {
          // Set value to use
          const valueToUse = pointSearchResult['index'] >= 0 ? pointSearchResult['previousItem']['value'] : 0;
          // Create new data point
          extraBreakPoints.push({
            startInSeconds: breakItem[timeType],
            endInSeconds: breakItem[timeType],
            value: valueToUse
          });
        }
      });
    });
    const mutatedDataset = JSON.parse(
      JSON.stringify({
        ...dataset,
        data: [...dataset['data'], ...extraBreakPoints]
      })
    );
    // Reassign symbol as it does get serialized/deserialized
    mutatedDataset['symbol'] = mutatedDataset['key'].includes('planned') ? PlannedDataSymbol : ActualDataSymbol;
    return mutatedDataset;
  };

  // Sets graph data based on changes in actual data and the metric being displayed
  useEffect(() => {
    let updatedGraphData;
    if (props.graphedMetric === 'volume') {
      updatedGraphData = updateGraphDatasets(props.graphedMetric, [
        'Label',
        'Inducted',
        'Stowed',
        'Sortation',
        'Outbound'
      ]);
    } else if (props.graphedMetric === 'headcount') {
      updatedGraphData = updateGraphDatasets(props.graphedMetric, ['Overall', 'Outbound', 'Rostered', 'Pick Stage']);
    } else if (props.graphedMetric === 'rate') {
      updatedGraphData = updateGraphDatasets(props.graphedMetric, ['Sortation', 'Outbound', 'Pick & Stage - Pick']);
    }

    // Find the maximum value to be plotted for the current graph datasets
    // This value (increased by 1%) will be used as the 'height' for the breaks areas
    const allValues = updatedGraphData
      .map((graphData) => graphData['data'])
      .flat()
      .map((datum) => datum['value']);
    const maxValue = Math.round(Math.max(...allValues) * 1.01);
    setMaxMetricGraphValue(maxValue);

    // Add points in datasets corresponding to break times
    // to ensure correct plotting of breaks periods
    const updatedGraphDataWithBreaks = [];
    updatedGraphData.forEach((graphData) => {
      updatedGraphDataWithBreaks.push(addBreakDatapointsToDataset(graphData, props.adherenceGraphData['breaks']));
    });

    // Add breaks dataset to collection
    updatedGraphDataWithBreaks.push({
      key: 'breaks',
      label: 'Breaks',
      color: '#04185aff',
      symbol: 'diamond',
      data: props.adherenceGraphData['breaks'] || []
    });

    // Convert data to strings so Victory treats them as categorical and doesn't attempt to sort.
    // These strings will be displayed in the Popover legend.
    const finalUpdatedGraphData = updatedGraphDataWithBreaks.map((graphDataset) => {
      const stringifiedData = (graphDataset['data'] || [])
        .sort((a, b) => a.startInSeconds - b.startInSeconds)
        .map((datum) => {
          const datumCopy = JSON.parse(JSON.stringify(datum));
          datumCopy.startInSeconds = new Date(datumCopy.startInSeconds * 1000)
            .toISOString()
            .substring(0, 16)
            .replace('T', ' ');
          datumCopy.endInSeconds = new Date(datumCopy.endInSeconds * 1000)
            .toISOString()
            .substring(0, 16)
            .replace('T', ' ');
          return datumCopy;
        });

      return { ...graphDataset, data: stringifiedData };
    });

    setGraphData(finalUpdatedGraphData);
  }, [props]);

  let minGraphSeconds = Number.MAX_SAFE_INTEGER;
  let maxGraphSeconds = 0;
  graphData.forEach((a) => {
    if (a.data.length > 0) {
      const minStart = new Date(a.data[0].startInSeconds + ':00+00:00').getTime() / 1000;
      const maxEnd = new Date(a.data[a.data.length - 1].endInSeconds + ':00+00:00').getTime() / 1000;

      if (minStart < minGraphSeconds) {
        minGraphSeconds = minStart;
      }
      if (maxGraphSeconds < maxEnd) {
        maxGraphSeconds = maxEnd;
      }
    }
  });

  const tickValues = generateIntervals(minGraphSeconds, maxGraphSeconds, 30);

  // TO DO: Specify the format for the title of the tooltip.
  // Currently showing time in seconds, need to be shown in hh:mm
  const plannedVersusActualsGraph = (
    <LegendProvider
      data={graphData}
      plugins={[victoryLinePlugin({ theme: graphTheme }), victoryTooltipPlugin({ showCrosshair: true })]}>
      {({ getLineProps, getScatterProps, voronoiContainerProps, Tooltip }) => (
        <Column spacing="none">
          <VictoryChart
            theme={graphTheme}
            width={window.innerWidth}
            height={600}
            padding={{ top: 40, left: 60, right: 60, bottom: 40 }}
            containerComponent={
              <VictoryVoronoiContainer
                {...voronoiContainerProps}
                labels={({ datum }) =>
                  datum.value === null || datum.value === undefined || isNaN(datum.value)
                    ? '-'
                    : convertToFixedDP(datum.value)
                }
              />
            }>
            <VictoryAxis
              style={{ tickLabels: { fontSize: 14, angle: 45, padding: 15 } }}
              tickFormat={(dateInSeconds) => {
                if (tickValues.includes(dateInSeconds)) {
                  return dateInSeconds.split(' ')[1];
                } else {
                  return '';
                }
              }}
            />
            <VictoryAxis
              dependentAxis
              style={{ tickLabels: { fontSize: 14 } }}
              tickFormat={(value, index) => (index > 0 ? value : '')}
            />
            {graphData
              .filter((graphDataset) => graphDataset['key'] !== 'breaks')
              .map(({ key, data }) => {
                const mergedStyle = getLineGraphStyle(getLineProps, key);
                return <VictoryLine key={key} data={data} x="endInSeconds" y="value" style={mergedStyle} />;
              })}
            {graphData
              .filter((graphDataset) => graphDataset['key'] === 'breaks')
              .map(({ key, data }) => {
                return data.map((breakWindow) => {
                  return (
                    <VictoryArea
                      data={[
                        { x: breakWindow['startInSeconds'], y: maxMetricGraphValue },
                        { x: breakWindow['endInSeconds'], y: maxMetricGraphValue }
                      ]}
                      style={{ data: { fill: 'rgba(237, 248, 207, 0.4)' } }}
                    />
                  );
                });
              })}
            {graphData
              .filter((graphDataset) => graphDataset['key'] === 'breaks')
              .map(({ key, data }) => (
                <VictoryScatter key={key} data={data} x="startInSeconds" y="value" {...getScatterProps(key)} />
              ))}
          </VictoryChart>
          <Legend direction="horizontal" />
        </Column>
      )}
    </LegendProvider>
  );

  return (
    <Column
      alignmentHorizontal="start"
      alignmentVertical="top"
      width="100%"
      spacing="500"
      spacingInset="none none 600 none">
      <Box type="outline" spacingInset="400" width="100%">
        <Column>{plannedVersusActualsGraph}</Column>
      </Box>
      <DynamicShiftPlanBetaTable
        graphedMetric={props.graphedMetric}
        stationLocalStablePlanTime={props.stationLocalStablePlanTime}
        adherenceGraphData={props.adherenceGraphData}
        isDynamicShiftPlanEnabled={props.isDynamicShiftPlanEnabled}
        isDynamicShiftPlanBetaEnabled={props.isDynamicShiftPlanBetaEnabled}
      />
    </Column>
  );
};
export default DynamicShiftPlanBetaGraph;
